// Блог Yolutap (17.10.2026): статьи Yolutap и коучей. Чистые функции —
// адреса, slug, hreflang, JSON-LD, пагинация. Без серверных импортов: файл
// читают маршруты, sitemap, /admin и scripts/verify-blog.mts.
//
// Таблица — public.blog_posts (20261101120000). Одна строка — одна статья
// на одном языке; переводы связаны translation_group. Автор — id коуча из
// src/data/coaches.ts (коучи живут в коде) или null — Yolutap.
import { COACHES, type CoachRecord } from "@/data/coaches";
import { latinSlug } from "@/lib/partner-slug";

export type BlogLang = "az" | "ru" | "en";
export const BLOG_LANGS: BlogLang[] = ["az", "ru", "en"];

/** Список статей: AZ — /az/bloq, RU — в корне /blog (как /pricing), EN — /en/blog. */
export const BLOG_PATH: Record<BlogLang, string> = { az: "/az/bloq", ru: "/blog", en: "/en/blog" };
export const blogPostPath = (lang: BlogLang, slug: string) => `${BLOG_PATH[lang]}/${slug}`;

export const BLOG_PAGE_SIZE = 12;
export const BLOG_DESCRIPTION_MAX = 155;
export const BLOG_TITLE_MAX = 200;
export const BLOG_SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const SITE = "https://yolutap.com";

/** Строка статьи, как её отдаёт сервер наружу (опубликованная). */
export type BlogPost = {
  id: string;
  lang: BlogLang;
  slug: string;
  title: string;
  description: string;
  body_md: string;
  cover_url: string | null;
  author_coach_id: string | null;
  translation_group: string;
  /** <title> страницы, если отличается от заголовка статьи (20261115120000). Нет — заголовок + « — Yolutap». */
  seo_title?: string | null;
  published_at: string | null;
  updated_at: string;
};
/** Карточка списка: без тела статьи, с временем чтения, посчитанным на сервере. */
export type BlogCard = Omit<BlogPost, "body_md" | "translation_group"> & { read_minutes: number };
/** Перевод статьи: язык и slug — для hreflang и переключателя языка. */
export type BlogTranslation = { lang: BlogLang; slug: string };

/** slug из заголовка — латиница, до 80 знаков; пусто — «post». */
export const blogSlugFromTitle = (title: string) => latinSlug(title, 80) || "post";

/** ?page= → номер страницы ≥ 1; мусор — 1. */
export function pageOf(raw: unknown): number {
  const n = typeof raw === "string" || typeof raw === "number" ? Number(raw) : NaN;
  return Number.isInteger(n) && n >= 1 && n <= 1000 ? n : 1;
}

export const pageCount = (total: number) => Math.max(1, Math.ceil(total / BLOG_PAGE_SIZE));

/** Адрес страницы списка: первая — без ?page. */
export const blogListHref = (lang: BlogLang, page: number) => (page <= 1 ? BLOG_PATH[lang] : `${BLOG_PATH[lang]}?page=${page}`);

/** Коуч-автор или null (Yolutap); неизвестный id — тоже null. */
export const coachOf = (id: string | null | undefined): CoachRecord | null => (id ? (COACHES.find((c) => c.id === id) ?? null) : null);

const COACHES_PATH: Record<BlogLang, string> = { ru: "/coaches", az: "/az/coaches", en: "/en/coaches" };
/** Страница коуча — его карточка на /coaches (якорь coach-<id>). */
export const coachHref = (lang: BlogLang, id: string) => `${COACHES_PATH[lang]}#coach-${id}`;

/** Автор без коуча — «Yolutap komandası» (29.10.2026; до этого просто «Yolutap»). */
export const TEAM_AUTHOR: Record<BlogLang, string> = { az: "Yolutap komandası", ru: "Команда Yolutap", en: "Yolutap team" };

/** Имя автора на языке страницы: коуч или команда Yolutap. */
export const authorName = (lang: BlogLang, coachId: string | null) => coachOf(coachId)?.name[lang] ?? TEAM_AUTHOR[lang];

/** Дата публикации: «17.10.2026». */
export function blogDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getUTCDate()).padStart(2, "0")}.${String(d.getUTCMonth() + 1).padStart(2, "0")}.${d.getUTCFullYear()}`;
}

/**
 * hreflang статьи — только на существующие опубликованные переводы (сама
 * статья всегда среди них) + x-default: азербайджанский перевод, если есть,
 * иначе русский, иначе английский (27.10.2026: x-default везде = /az).
 */
export function postAlternates(self: BlogTranslation, translations: BlogTranslation[]): { hreflang: string; href: string }[] {
  const byLang = new Map<BlogLang, string>();
  for (const t of [self, ...translations]) if (!byLang.has(t.lang)) byLang.set(t.lang, t.slug);
  const out: { hreflang: string; href: string }[] = BLOG_LANGS.filter((l) => byLang.has(l)).map((l) => ({ hreflang: l, href: SITE + blogPostPath(l, byLang.get(l)!) }));
  const xd = (["az", "ru", "en"] as BlogLang[]).find((l) => byLang.has(l))!;
  out.push({ hreflang: "x-default", href: SITE + blogPostPath(xd, byLang.get(xd)!) });
  return out;
}

const HOME: Record<BlogLang, string> = { az: "/az", ru: "/ru", en: "/en" };
export const BLOG_NAME: Record<BlogLang, string> = { az: "Bloq", ru: "Блог", en: "Blog" };
const LOGO = `${SITE}/assets/logo/yolutap-icon-512.png`;

/** JSON-LD Article: author — Person (коуч) или Organization (Yolutap), publisher — Organization. */
export function articleJsonLd(p: BlogPost) {
  const coach = coachOf(p.author_coach_id);
  const url = SITE + blogPostPath(p.lang, p.slug);
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: p.title,
    description: p.description,
    inLanguage: p.lang,
    url,
    mainEntityOfPage: url,
    ...(p.cover_url ? { image: p.cover_url } : {}),
    ...(p.published_at ? { datePublished: p.published_at } : {}),
    dateModified: p.updated_at,
    author: coach
      ? { "@type": "Person", name: coach.name[p.lang], url: SITE + coachHref(p.lang, coach.id), image: SITE + coach.photo }
      : { "@type": "Organization", name: "Yolutap", url: SITE },
    publisher: { "@type": "Organization", name: "Yolutap", url: SITE, logo: { "@type": "ImageObject", url: LOGO } },
  };
}

/** BreadcrumbList статьи: Yolutap → Блог → статья. */
export function blogBreadcrumbJsonLd(p: Pick<BlogPost, "lang" | "slug" | "title">) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Yolutap", item: SITE + HOME[p.lang] },
      { "@type": "ListItem", position: 2, name: BLOG_NAME[p.lang], item: SITE + BLOG_PATH[p.lang] },
      { "@type": "ListItem", position: 3, name: p.title, item: SITE + blogPostPath(p.lang, p.slug) },
    ],
  };
}

/** Строка статьи для sitemap: адреса переводов той же группы — по ним hreflang. */
export type SitemapBlogRow = { lang: BlogLang; slug: string; translation_group: string; updated_at: string | null };

// ── карточки и обложки (19.10.2026, вариант A) ──────────────────────────

/** Время чтения: слова Markdown / 200, вверх, не меньше минуты. */
export function readMinutes(md: string): number {
  const text = md
    .replace(/\]\([^)]*\)/g, "]") // адреса ссылок и картинок — не слова
    .replace(/[#>*_`~[\]!|-]/g, " ");
  const words = text.split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w)).length;
  return Math.max(1, Math.ceil(words / 200));
}

/** FNV-1a: один и тот же slug — одна и та же обложка на любой машине. */
export function slugHash(slug: string): number {
  let h = 0x811c9dc5;
  for (const ch of slug) {
    h ^= ch.codePointAt(0)!;
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Палитры обложки без картинки: всегда от тёмно-зелёного #17404c к бирюзе,
 * пятно — мята или (в одной палитре) лёгкий оранжевый акцент.
 */
export const COVER_PALETTES = [
  { to: "#1f6b70", spot: "rgba(159, 220, 201, 0.34)" },
  { to: "#2f8f86", spot: "rgba(242, 163, 58, 0.22)" },
  { to: "#24777a", spot: "rgba(159, 220, 201, 0.26)" },
  { to: "#2b8580", spot: "rgba(255, 255, 255, 0.16)" },
] as const;

/** Палитра по slug; совпала с соседней слева — следующая, чтобы соседи отличались. */
export function coverPalette(slug: string, previous: number | null = null): number {
  const n = slugHash(slug) % COVER_PALETTES.length;
  return n === previous ? (n + 1) % COVER_PALETTES.length : n;
}

export type CoverRing = { size: number; x: "left" | "right"; y: "top" | "bottom"; offset: number };

/** Обложка без картинки: палитра, место пятна и 1–2 тонких кольца у края — всё от hash(slug). */
export function coverLayout(slug: string, previous: number | null = null) {
  const h = slugHash(slug);
  const palette = coverPalette(slug, previous);
  const corners: Array<Pick<CoverRing, "x" | "y">> = [
    { x: "right", y: "top" },
    { x: "left", y: "bottom" },
    { x: "right", y: "bottom" },
    { x: "left", y: "top" },
  ];
  const first = (h >>> 3) % 4;
  const rings: CoverRing[] = [{ ...corners[first], size: 150 + ((h >>> 5) % 5) * 20, offset: 40 + ((h >>> 8) % 4) * 10 }];
  if ((h >>> 11) % 2) rings.push({ ...corners[(first + 2) % 4], size: 90 + ((h >>> 13) % 4) * 20, offset: 30 + ((h >>> 16) % 3) * 10 });
  return { palette, spotX: 20 + ((h >>> 18) % 61), spotY: 15 + ((h >>> 24) % 56), rings };
}

/**
 * Тема статьи. Колонки темы в базе нет: статья коуча — «Карьера» (компас),
 * статья Yolutap — «Учёба за рубежом» (глобус).
 */
export type BlogTopic = "career" | "abroad";
export const blogTopic = (coachId: string | null | undefined): BlogTopic => (coachOf(coachId) ? "career" : "abroad");
